import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import { getGroups, Group, getGroupStandings, GroupStanding } from '../../api/groupsApi';
import { getUserMatchBets, Match } from '../../api/matchesApi';
import Table, { TableColumn } from '../../components/Table';
import MatchCard from '../../components/MatchCard';
import { usePlaceMatchBet } from '../../hooks/usePlaceMatchBet';

// Helper for sorting standings as in backend
function sortStandings(standings: GroupStanding[]) {
  return [...standings].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.goal_difference !== a.goal_difference) return b.goal_difference - a.goal_difference;
    if (b.goals_for !== a.goals_for) return b.goals_for - a.goals_for;
    // If you want to support fair play points, add here (ascending)
    if (a.team_name < b.team_name) return -1;
    if (a.team_name > b.team_name) return 1;
    return 0;
  });
}

interface BetPrediction {
  matchId: string;
  homeScore: number;
  awayScore: number;
}

const GroupBets: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [standings, setStandings] = useState<GroupStanding[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPredicted, setShowPredicted] = useState(false);

  // Use shared hook for placing bets
  const { placeBet, isSubmitting } = usePlaceMatchBet({
    onSuccess: async (matchId) => {
      // Refetch standings and matches after successful bet
      if (selectedGroup) {
        const [standingsData, matchesData] = await Promise.all([
          getGroupStandings(selectedGroup.id),
          getUserMatchBets()
        ]);
        setStandings(standingsData);
        setMatches(matchesData.filter(m => m.group_id === selectedGroup.id));
      }
    },
    onError: (err) => {
      alert('שגיאה בשמירה. נסה שוב.');
    }
  });

  useEffect(() => {
    const fetchGroups = async () => {
      const groupsData = await getGroups();
      setGroups(groupsData);
      if (groupsData.length > 0) {
        setSelectedGroup(groupsData[0]);
      }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    if (!selectedGroup) return;
    setLoading(true);
    const fetchData = async () => {
      const [standingsData, matchesData] = await Promise.all([
        getGroupStandings(selectedGroup.id),
        getUserMatchBets()
      ]);
      setStandings(standingsData);
      setMatches(matchesData.filter(m => m.group_id === selectedGroup.id));
      setLoading(false);
    };
    fetchData();
  }, [selectedGroup]);

  // Simulate standings based on user's real bets for future matches
  const getPredictedStandings = useCallback(() => {
    // Deep clone standings
    const teamMap: Record<number, GroupStanding> = {};
    standings.forEach(s => {
      teamMap[s.team_id] = { ...s };
    });
    // Only consider future matches with a real user bet
    matches.forEach(match => {
      if (
        match.status === 'SCHEDULED' &&
        match.user_bet_home_score !== null &&
        match.user_bet_home_score !== undefined &&
        match.user_bet_away_score !== null &&
        match.user_bet_away_score !== undefined &&
        match.home_team_id &&
        match.away_team_id
      ) {
        const home = teamMap[match.home_team_id];
        const away = teamMap[match.away_team_id];
        if (!home || !away) return;
        // Apply user bet
        home.played += 1;
        away.played += 1;
        home.goals_for += match.user_bet_home_score;
        home.goals_against += match.user_bet_away_score;
        away.goals_for += match.user_bet_away_score;
        away.goals_against += match.user_bet_home_score;
        home.goal_difference = home.goals_for - home.goals_against;
        away.goal_difference = away.goals_for - away.goals_against;
        if (match.user_bet_home_score > match.user_bet_away_score) {
          home.won += 1;
          home.points += 3;
          away.lost += 1;
        } else if (match.user_bet_home_score < match.user_bet_away_score) {
          away.won += 1;
          away.points += 3;
          home.lost += 1;
        } else {
          home.drawn += 1;
          away.drawn += 1;
          home.points += 1;
          away.points += 1;
        }
      }
    });
    return sortStandings(Object.values(teamMap));
  }, [standings, matches]);

  // Handle real bet placement from MatchCard
  const handlePlaceBet = async (matchId: string, homeScore: string, awayScore: string, winnerId?: number | null) => {
    const match = matches.find(m => m.id === matchId);
    if (!match) return;
    await placeBet(matchId, homeScore, awayScore, winnerId, match);
  };

  const standingsColumns: TableColumn<GroupStanding>[] = [
    { key: 'team_logo', title: '', render: (item) => <img src={item.team_logo} alt={item.team_name} className="h-6 w-6 rounded-full" />, width: '3rem' },
    { key: 'team_name', title: 'קבוצה', render: (item) => item.team_name, width: '100px' },
    { key: 'played', title: 'מש', render: (item) => item.played, width: '24px' },
    { key: 'won', title: 'נ', render: (item) => item.won, width: '24px' },
    { key: 'drawn', title: 'ת', render: (item) => item.drawn, width: '24px' },
    { key: 'lost', title: 'ה', render: (item) => item.lost, width: '24px' },
    { key: 'goals_for', title: 'ז', render: (item) => item.goals_for, width: '24px' },
    { key: 'goals_against', title: 'נ', render: (item) => item.goals_against, width: '24px' },
    { key: 'goal_difference', title: '+/-', render: (item) => item.goal_difference, width: '24px' },
    { key: 'points', title: 'נק', render: (item) => <span className="font-bold text-green-400">{item.points}</span>, width: '32px' },
  ];

  const displayedStandings = showPredicted ? getPredictedStandings() : standings ;

  return (
    <Layout>
      <div>
        <h1 className="text-2xl font-bold text-white mb-6">בתים</h1>
        <div className="max-w-xl mx-auto mb-4 flex items-center justify-start gap-3">
          <label className="text-gray-300 font-medium">בחר בית:</label>
          <select
            className="bg-gray-800 border border-gray-700 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedGroup?.id || ''}
            onChange={e => {
              const group = groups.find(g => g.id === Number(e.target.value));
              setSelectedGroup(group || null);
            }}
          >
            {groups.map(group => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </div>
        {/* <div className="mb-4 flex items-center gap-4">
          <span className="text-gray-300 font-medium">הצג:</span>
          <button
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${!showPredicted ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
            onClick={() => setShowPredicted(false)}
          >
            טבלה אמיתית
          </button>
          <button
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${showPredicted ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}
            onClick={() => setShowPredicted(true)}
          >
            טבלת תחזית
          </button>
        </div> */}
        <div className="mb-8">
          {/* <h2 className="text-xl font-semibold mb-4">טבלת בית</h2> */}
          <Table
            className="w-full max-w-lg"
            data={displayedStandings}
            columns={standingsColumns}
            keyExtractor={item => item.team_id}
            isLoading={loading}
            qualifyingPositions={2}
            emptyMessage="לא נמצאו נתונים"
            compact
          />
        </div>
        <div>
          {/* <h2 className="text-xl font-semibold mb-4">משחקי הבית</h2> */}
          {loading ? (
            <div className="text-center text-gray-400">טוען...</div>
          ) : matches.length === 0 ? (
            <div className="text-center text-gray-400">אין משחקים לבית זה</div>
          ) : (
            <div className="flex flex-col gap-4">
              {matches.map(match => (
                <MatchCard key={match.id} match={match} onPlaceBet={handlePlaceBet} compact={true} />
              ))}
              {matches.some(m => isSubmitting(m.id)) && (
                <div className="text-center text-blue-400">שומר...</div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GroupBets; 